import { ReactNode, useEffect, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { trackEventActions, trackEventCategories, trackEventNames, useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { CtaComponentLabels } from '../../../common/labels/cta-component.labels';
import { useComponentIcon } from '../../../common/hooks/useComponentIcon';
import { useComponentVisibility } from '../../../common/hooks/useComponentVisibility';
import { HiddenContentComponent } from '../hidden-content-component/hidden-content-component';
import { selectSavingsState } from '../../../common/store/savings.slice';
import { useAppSelector } from '../../../common/store/hooks';
import { selectAssetsState } from '../../../common/store/assets/assets.slice';
import { selectSimulationState } from '../../../common/store/simulation/simulation.slice';

/* eslint-disable-next-line */
export interface CtaModalComponentProps {
  labels: CtaComponentLabels;
  children: ReactNode;
  isOpen?: boolean;
  showTitle?: boolean;
  renderChildrenAsHiddenContent?: boolean;
  isDependentOnAssets?: boolean;
  isSecondaryCta?: boolean;
}

export function CtaModalComponent(props: CtaModalComponentProps) {
  const [isDependentOnAssets] = useState<boolean>(props.isDependentOnAssets ??false);
  const [isCtaPopupOpen, setIsCtaPopupOpen] = useState<boolean>(props.isOpen ?? false);
  const trackEvent = useTrackEvent();
  const ctaIcon = useComponentIcon("CTA");
  const isCtaHidden = useComponentVisibility("CTA");
  const assets = useAppSelector(selectAssetsState);
  const savings = useAppSelector(selectSavingsState);
  const simulation = useAppSelector(selectSimulationState);
  const [isDisabled, setIsDisabled] = useState<boolean | undefined>(simulation.isLoading);

  const childrenRef = useRef<HTMLDivElement | null>(null);

  const open = () => {
    trackEvent({ category: trackEventCategories.RESULTS, action: trackEventActions.OPEN, name: trackEventNames.CTA_FINAL });
    setIsCtaPopupOpen(true);
  }

  const close = () => {
    trackEvent({ category: trackEventCategories.RESULTS, action: trackEventActions.CLOSE, name: trackEventNames.CTA_FINAL });
    setIsCtaPopupOpen(false);
  }

  useEffect(() => {
    setIsCtaPopupOpen(props.isOpen ?? false);
    if (props.isOpen && childrenRef?.current) {
      childrenRef.current.style.visibility = 'visible'
    } else if (childrenRef?.current) {
      childrenRef.current.style.visibility = 'collapse'
    }
  }, [props.isOpen]);

  useEffect(() => {
    const hasAssets = assets.cash === 0
      && assets.bonds === 0
      && assets.equity === 0
      && assets.realEstate === 0
      && savings.lumpsum === 0
      && savings.monthly === 0
    setIsDisabled((isDependentOnAssets && hasAssets) || simulation.isLoading);
  }, [isDependentOnAssets, assets, savings, simulation.isLoading]);

  return (!isCtaHidden ?
      <>
      <Popup
        open={isCtaPopupOpen}
        onClose={close}
        position="center center"
        arrow={false}
        modal={true}>
            <div className="cta-component">
                {props.showTitle && <div className="modal-title modal-title--empty">
                    <span
                      className="modal-title-icon button-close icon-times"
                      style={{ alignSelf: 'center', textAlign: 'center', marginRight: 0 }}
                      onClick={close}
                      />
                </div>}
                {!props.showTitle && <span
                  className="modal-title-icon button-close icon-times"
                  style={{ position: 'absolute', right: 20, top: 20, alignSelf: 'center', textAlign: 'center', marginRight: 0 }}
                  onClick={close}
                />}
                {props.showTitle && <div className="row" style={{ display: 'flex', height: '1px', background: 'lightgray' }} />}
                {props.renderChildrenAsHiddenContent ?
                  <div dangerouslySetInnerHTML={{ __html: childrenRef.current?.innerHTML || '' }}></div>
                  : props.children}
            </div>
        </Popup>
        <button
          type="button"
          disabled={isDisabled}
          onClick={open}
          className={`button cta-button ${ctaIcon ? 'button-with-icon' : ''} ${props.isSecondaryCta ? 'cta-button--secondary' : ''} ${isDisabled ? 'cta-button--disabled' : ''}`}>
            {props.labels.PERSONA.TITLE}
            {ctaIcon && <span className="cta-button-icon"
              dangerouslySetInnerHTML={{ __html: ctaIcon ?? '' }}></span>}
        </button>
        {props.renderChildrenAsHiddenContent && <HiddenContentComponent ref={childrenRef}>
          {props.children}
        </HiddenContentComponent>}
      </>
      :
        <a
          type="button"
          href={props.labels.SUBMIT_LOCATION}
          target='_blank'
          rel='noreferrer'
          className={`button cta-button ${ctaIcon ? 'button-with-icon' : ''} ${props.isSecondaryCta ? 'cta-button--secondary' : ''}`}>
            {props.labels.PERSONA.TITLE}
            {ctaIcon && <span className="cta-button-icon"
              dangerouslySetInnerHTML={{ __html: ctaIcon ?? '' }}></span>}
        </a>
  );
}

export default CtaModalComponent;
